import * as t from 'io-ts';

import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Constants.
 */

export enum LocalApplicationServerAuthenticationStrategiesEnum {
  NONE = 'none',
  BASIC = 'basic',
  BEARER = 'bearer',
  OAUTH2 = 'oauth2',
}
const RLocalApplicationServerAuthenticationStrategy = makeEnumRuntimeType(
  LocalApplicationServerAuthenticationStrategiesEnum,
);

/*
 * Runtime types.
 */

const RLocalApplicationServeBaseAuthenticationConfig = t.type({
  type: RLocalApplicationServerAuthenticationStrategy,
});

const RLocalApplicationServerNoneAuthenticationConfig = t.intersection([
  RLocalApplicationServeBaseAuthenticationConfig,
  t.type({
    type: t.literal(LocalApplicationServerAuthenticationStrategiesEnum.NONE),
  }),
]);

const RLocalApplicationServerBasicAuthenticationConfig = t.intersection([
  RLocalApplicationServeBaseAuthenticationConfig,
  t.type({
    type: t.literal(LocalApplicationServerAuthenticationStrategiesEnum.BASIC),
  }),
]);

const RLocalApplicationServerBearerAuthenticationConfig = t.intersection([
  RLocalApplicationServeBaseAuthenticationConfig,
  t.type({
    type: t.literal(LocalApplicationServerAuthenticationStrategiesEnum.BEARER),
  }),
]);

const RLocalApplicationServerOAuth2AuthenticationConfig = t.intersection([
  RLocalApplicationServeBaseAuthenticationConfig,
  t.type({
    type: t.literal(LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2),
    clientId: t.string,
    clientSecret: t.string,
    scope: t.string,
    tokenUrl: t.string,
    authorizationUrl: t.string,
  }),
]);

const RLocalApplicationServerAuthenticationConfig = t.taggedUnion('type', [
  RLocalApplicationServerNoneAuthenticationConfig,
  RLocalApplicationServerBasicAuthenticationConfig,
  RLocalApplicationServerBearerAuthenticationConfig,
  RLocalApplicationServerOAuth2AuthenticationConfig,
]);

const RLocalApplicationServerDirty = t.readonly(
  t.type({
    id: t.Integer,
    installationUrl: t.string,
    origin: t.string,
    url: t.string,
    authenticationConfig: RLocalApplicationServerAuthenticationConfig,
    createdAt: t.Integer,
    updatedAt: t.Integer,
  }),
);

/*
 * Types.
 */

export interface LocalApplicationServerModel extends t.TypeOf<typeof RLocalApplicationServerDirty> {}
export interface LocalApplicationServerNoneAuthenticationConfigModel
  extends t.TypeOf<typeof RLocalApplicationServerNoneAuthenticationConfig> {}
export interface LocalApplicationServerBasicAuthenticationConfigModel
  extends t.TypeOf<typeof RLocalApplicationServerBasicAuthenticationConfig> {}
export interface LocalApplicationServerBearerAuthenticationConfigModel
  extends t.TypeOf<typeof RLocalApplicationServerBearerAuthenticationConfig> {}
export interface LocalApplicationServerOAuth2AuthenticationConfigModel
  extends t.TypeOf<typeof RLocalApplicationServerOAuth2AuthenticationConfig> {}
export const RLocalApplicationServer = t.clean<LocalApplicationServerModel>(
  RLocalApplicationServerDirty as any,
);

export type LocalApplicationServerAuthenticationConfigModel =
  | LocalApplicationServerNoneAuthenticationConfigModel
  | LocalApplicationServerBasicAuthenticationConfigModel
  | LocalApplicationServerBearerAuthenticationConfigModel
  | LocalApplicationServerOAuth2AuthenticationConfigModel;

/*
 * IO methods.
 */

function parseServerAuthenticationConfig(src: any): LocalApplicationServerAuthenticationConfigModel {
  switch (src.authentication_config?.type) {
    case LocalApplicationServerAuthenticationStrategiesEnum.NONE:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.NONE,
      };

    case LocalApplicationServerAuthenticationStrategiesEnum.BASIC:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.BASIC,
      };

    case LocalApplicationServerAuthenticationStrategiesEnum.BEARER:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.BEARER,
      };

    case LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2,
        clientId: src.authentication_config.client_id,
        clientSecret: src.authentication_config.client_secret,
        scope: src.authentication_config.scope,
        tokenUrl: src.authentication_config.token_url,
        authorizationUrl: src.authentication_config.authorization_url,
      };

    default:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.NONE,
      };
  }
}

export function parseLocalApplicationServer(src: any): LocalApplicationServerModel {
  return {
    id: src.id,
    url: src.url,
    installationUrl: src.installation_url,
    origin: src.origin,
    authenticationConfig: parseServerAuthenticationConfig(src),
    createdAt: src.created_at,
    updatedAt: src.updated_at,
  };
}

export function parseLocalApplicationServers(src: any): ReadonlyArray<LocalApplicationServerModel> {
  return src.items.map(parseLocalApplicationServer);
}

export function serializeLocalApplicationServerAuthenticationConfig(
  authenticationConfig: LocalApplicationServerAuthenticationConfigModel,
): any {
  switch (authenticationConfig.type) {
    case LocalApplicationServerAuthenticationStrategiesEnum.NONE:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.NONE,
      };

    case LocalApplicationServerAuthenticationStrategiesEnum.BASIC:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.BASIC,
      };

    case LocalApplicationServerAuthenticationStrategiesEnum.BEARER:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.BEARER,
      };

    case LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2,
        client_id: authenticationConfig.clientId,
        client_secret: authenticationConfig.clientSecret,
        scope: authenticationConfig.scope,
        token_url: authenticationConfig.tokenUrl,
        authorization_url: authenticationConfig.authorizationUrl,
      };

    default:
      return {
        type: LocalApplicationServerAuthenticationStrategiesEnum.NONE,
      };
  }
}

/*
 * API types.
 */

export const ApiLocalApplicationServer = makeApiType(
  'localApplicationServer',
  RLocalApplicationServer,
  parseLocalApplicationServer,
);
export const ApiLocalApplicationServers = makeApiType(
  'localApplicationServers',
  t.readonlyArray(RLocalApplicationServer),
  parseLocalApplicationServers,
);
